import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { IApiClaimsHistory } from '../../../interfaces/IJob';
import { IServiceJob } from '../../pages/claim-history/claim-history.interface';
import { DataService } from '../../../services/data/data.service';
import { ServiceRequestService } from '../../../services/service-request/service-request.service';
import { switchMap, take, map, catchError, of } from 'rxjs';

import { IAvailability, IAvailabilityResponse, IItems, IRecallAvailabilityPayload, ISelectedProduct } from '../../pages/service-request/service-request.interface';
import { RouterService } from '../../../services/router/router.service';
import { APP_ROUTES } from '../../../utils/route.constant';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { UtilitiesService } from '../../../services/utilities/utilities.service';
import { ALL_PAGES, CLOSE_RECALL, DASHBOARD_PAGE_LOCATION, ERR_EVENT, MODAL_OPEN_FROM_DASHBOARD_PAGE, MODAL_OPEN_FROM_LATEST_ACTIVITY, MODAL_OPEN_FROM_SR_DETAILS_PAGE, MODAL_OPEN_FROM_SR_HISTORY_PAGE, MYACCOUNT_LOGGEDIN_SR, NO_DIFFERENT_ISSUE, RECALL_ERROR_PHONE_NUMBER, REQUEST_RECALL_SERVICE, SR_LIST_PAGE, SR_PM_INIT_CATEGORY_SELECTION_PAGE, SR_START, SR_STATUS_PAGE } from '../../../services/ga4/ga4.constant';
import { serviceJobIdRecallFlag } from '../../../utils/feature-flags';
import { VivrService } from '../../../services/vivr/vivr.service';
import { CMP } from '../../../utils/constants';

@Component({
  selector: 'app-recall-claim-dialog',
  templateUrl: './recall-claim-dialog.component.html',
  styleUrl: './recall-claim-dialog.component.scss'
})
export class RecallClaimDialogComponent implements OnChanges {

  @Input() showRecallModal: boolean = false;
  @Input() selectedJob: IApiClaimsHistory | IServiceJob | null = null;
  @Input() location: string = '';

  selectedItem: IItems | null = null;
  isLoading: boolean = false;
  isDifferentIssueLoading: boolean = false;
  errorMessage: string = '';
  recallErrorPhone: string = '';
  isAPOSPCompletedJob: boolean = false;
  readonly recallErrorPhoneNumber = RECALL_ERROR_PHONE_NUMBER;

  constructor(private dataService: DataService,
    private serviceRequestService: ServiceRequestService,
    private routerService: RouterService,
    private gaService: Ga4Service,
    private utilService: UtilitiesService,
    private vivrService: VivrService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedJob']) {
      this.isAPOSPCompletedJob = false;
      this.recallErrorPhone = '';

      if (this.selectedJob) {
        this.isAPOSPCompletedJob = this.utilService.isAPOSPJob(this.selectedJob.serviceProviderAccountNumber)
          && this.selectedJob.serviceJobStatus === CMP;

        if (this.isAPOSPCompletedJob) {
          const contract = this.dataService.contractData$.getValue();
          this.recallErrorPhone = contract?.baseContract.customerServicePhoneNumber ?? '';
        }

        if (this.selectedJob.models.length > 0) {
          this.getItemsAndLocations();
        }
      }
    }
  }

  getItemsAndLocations(): void {
    if (this.selectedJob) {
      const item: IItems = {
        label: this.selectedJob.models[0].itemName,
        icon: this.selectedJob.models[0].itemId,
        isHover: false,
        id: this.selectedJob.models[0].itemId,
        burkeId: '',
        category: this.selectedJob.models[0].itemTrade,
        value: this.selectedJob.models[0].tradeId,
        serialNumber: this.selectedJob.models[0].serialNumber || null,
        modelId: this.selectedJob.models[0].modelId || null
      };
      this.selectedItem = item;
      this.dataService.serviceRequestSelectedCategoryValue$.next({ label: this.selectedJob.models[0].itemTrade, value: this.selectedJob.models[0].tradeId });
    }
  }

  getSymptomDesc(job: IApiClaimsHistory | IServiceJob | null): string {
    return job?.problemCode ?? '';
  }

  getIcon(value: string): string {
    if (value) {
      return this.serviceRequestService.iconLookUp(value);
    }
    return '';
  }

  scheduleRecallService() {
    const contract = this.dataService.contractData$.getValue();
    if (!contract || !this.selectedJob || !this.selectedItem) { return; }

    const planType = this.utilService.getPlanType(contract);
    this.gaService.sendSrFlowEvent(
      SR_START,
      planType,
      MYACCOUNT_LOGGEDIN_SR,
      true,
      !!this.selectedItem.modelId,
      !!this.selectedItem.serialNumber,
      'Recall_Item'
    );
    this.gaService.buttonClickEvent(this.getModalOpenFromLocation(), REQUEST_RECALL_SERVICE);

    this.dataService.serviceRequestSelectedProducts$.next([]);
    this.dataService.serviceRequestSelectedItem$.next(this.selectedItem);
    this.dataService.recallJob$.next(this.selectedJob);
    this.dataService.isRecall$.next(true);

    const payload: IRecallAvailabilityPayload = {
      zipCode: contract.baseContract.propertyAddress.zip,
      requestProductLine: this.selectedItem.icon,
      sourceSystemName: 'PMC',
      requestDatesNeeded: 14,
      brand: this.selectedJob.models[0].brand,
      availableProviderAccount: this.selectedJob.serviceProviderAccountNumber ?? '',
    };

    if (serviceJobIdRecallFlag) {
      payload.serviceJobId = this.selectedJob.serviceJobId ?? '';
    }

    this.isLoading = true;

    this.vivrService.getServiceJobDetails(this.selectedJob.serviceOrderId).pipe(
      take(1),
      catchError(() => of(null as null)),
      switchMap((details: { jobStatusResponse?: { complaintCode?: string } } | null) => {
        const complaintCode = details?.jobStatusResponse?.complaintCode ?? '';
        return this.serviceRequestService.getRecallAvailability(contract.baseContract.contractNumber, payload).pipe(
          take(1),
          map((data: IAvailability) => ({ data, complaintCode }))
        );
      })
    ).subscribe({
      next: ({ data, complaintCode }) => {
        this.isLoading = false;
        if (data.successFlag === 'N' || data.availabilityFound === 'N' || !data.availabilityDates?.length) {
          this.dataService.isRecall$.next(false);
          this.showRecallError(contract.baseContract.customerServicePhoneNumber);
          return;
        }
        const selectedProduct = this.buildSelectedProduct(complaintCode);
        this.dataService.serviceRequestSelectedProducts$.next([selectedProduct]);

        const availabilityData: IAvailabilityResponse = {
          items: [{
            availability: {
              successFlag: 'Y',
              responseMessage: 'NORMAL',
              availabilityFound: 'Y',
              requestControlID: data.requestControlID,
              availabilityDates: [data.availabilityDates[0]],
            },
            id: this.selectedItem!.icon,
            requestProductLine: this.selectedItem!.icon,
          }],
          successFlag: 'Y',
          token: '',
          contractNumber: contract.baseContract.contractNumber,
          statusCode: 200,
        };
        this.dataService.serviceRequestAvailablity$.next(availabilityData);
        this.dataService.serviceRequestSelectedCategoryValue$.next(null);
        this.dataService.serviceRequestSelectedItem$.next(null);
        this.dataService.isAddItemClicked$.next(false);
        this.routerService.goto(APP_ROUTES.SER_REQ_REVIEW_REQ_PAGE);
      },
      error: () => {
        this.isLoading = false;
        this.dataService.isRecall$.next(false);
        this.showRecallError(contract.baseContract.customerServicePhoneNumber);
      }
    });
  }

  private buildSelectedProduct(complaintCode: string = ''): ISelectedProduct {
    const job = this.selectedJob!;
    const model = job.models[0];
    return {
      category: model.itemTrade,
      categoryId: model.tradeId,
      item: model.itemName,
      itemId: model.itemId,
      location: model.unitLocation,
      locationCode: model.unitLocation,
      brand: model.brand,
      brandName: model.brand,
      otherBrand: '',
      model: model.modelId === 'VERIFY' ? '' : (model.modelId ?? ''),
      serialNo: model.serialNumber === 'VERIFY' ? '' : (model.serialNumber ?? ''),
      burkeInfo: {
        symptomId: 0,
        legacyCode: complaintCode,
        symptomDescription: this.getSymptomDesc(job) ||  '',
        productTypeId: '',
        itemId: model.itemId,
        modelId: model.modelId === 'VERIFY' ? '' : (model.modelId ?? ''),
        brandId: 0,
      },
      isAutomaticDecisionElement: false,
      replacementDeductibleDue: 0,
      eligible: false,
      deductible: 0,
      isEditFlag: false,
      symptoms: [],
      brands: [],
    };
  }

  private showRecallError(phone: string): void {
    this.recallErrorPhone = phone;
    const errMsg = 'Unfortunately, we are unable to book your return service at the moment. Please contact Customer Service at ' + phone + ' to schedule your appointment.';
    this.errorMessage = errMsg;
    this.gaService.messageEvent(ERR_EVENT, this.utilService.sanitizeErrorMessage(errMsg), ALL_PAGES);
  }

  closeRecallDialog() {
    this.dataService.isRecall$.next(false);
    this.dataService.recallJob$.next(null);
    this.dataService.recallDialogClose$.next();
    this.gaService.buttonClickEvent(this.getModalOpenFromLocation(), CLOSE_RECALL);
  }

  RedirectToWhatIsIssue() {
    if (!this.selectedItem || !this.selectedJob) {
      return;
    }
    this.isDifferentIssueLoading = true;
    this.dataService.isRecall$.next(false);
    this.dataService.recallJob$.next(this.selectedJob);
    this.dataService.isDifferentIssueNewJob$.next(true);
    this.dataService.serviceRequestSelectedProducts$.next([]);
    this.dataService.serviceRequestSelectedItem$.next(this.selectedItem);
    this.gaService.buttonClickEvent(this.getModalOpenFromLocation(), NO_DIFFERENT_ISSUE);
    this.routerService.goto(APP_ROUTES.SER_REQ_WHAT_IS_ISSUE_PAGE);
  }

  private readonly modalOpenFromLocationMap: { [key: string]: string } = {
    [DASHBOARD_PAGE_LOCATION]: MODAL_OPEN_FROM_DASHBOARD_PAGE,
    [SR_LIST_PAGE]: MODAL_OPEN_FROM_SR_HISTORY_PAGE,
    [SR_STATUS_PAGE]: MODAL_OPEN_FROM_SR_DETAILS_PAGE,
    [SR_PM_INIT_CATEGORY_SELECTION_PAGE]: MODAL_OPEN_FROM_LATEST_ACTIVITY,
  };

  private getModalOpenFromLocation(): string {
    return this.modalOpenFromLocationMap[this.location] ?? this.location;
  }
}